import { Component, computed, inject, input, Signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { CourseVariablesService } from '../../../domains/academy/course/services/course-variables.service';
import { VariableTextAreaComponent } from '../../../domains/organization/organization/components/variable-text-area/variable-text-area.component';
import { DoubleIconButtonComponent } from '../double-icon-button/double-icon-button.component';
import { BadgeComponent } from '../badge/badge.component';
import { LanguageCode } from '../../../domains/organization/organization/models/CourseVariables';
import { ResetTextButtonComponent } from '../reset-text-button/reset-text-button.component';

@Component({
  selector: 'app-variable-block',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslocoDirective,
    VariableTextAreaComponent,
    DoubleIconButtonComponent,
    BadgeComponent,
    ResetTextButtonComponent,
  ],
  templateUrl: './variable-block.component.html',
})
export class VariableBlockComponent {
  variableName = input.required<string>();

  private readonly courseVariablesService = inject(CourseVariablesService);
  private readonly translocoService = inject(TranslocoService);

  // those are only for the view
  languageCodeDE = LanguageCode.DE;
  languageCodeEN = LanguageCode.EN;

  customizedVariable = computed(() => {
    const germanInput = this.courseVariablesService.getSignal(
      this.variableName(),
      LanguageCode.DE,
    );
    const englishInput = this.courseVariablesService.getSignal(
      this.variableName(),
      LanguageCode.EN,
    );

    return germanInput() !== '' || englishInput() !== '';
  });

  hasTranslation = computed(() => {
    const key = `portal.courseSettings.topicAreaSettings.values.${this.variableName()}`;

    return (
      this.translocoService.translate(
        key,
        {},
        LanguageCode.EN.toLowerCase(),
      ) !== key ||
      this.translocoService.translate(
        key,
        {},
        LanguageCode.DE.toLowerCase(),
      ) !== key
    );
  });

  maxLength = computed(() => {
    const longTextKeys = [
      'CyberIncidentExample',
      'DPContactWay',
      'DPIncidentExample',
      'LinkFileXchange',
      'PasswordManagerTip',
      'ReportPhishing',
    ];
    return longTextKeys.includes(this.variableName()) ? 255 : 50;
  });

  hasStandardValue = computed(
    () =>
      this.hasTranslation() &&
      (this.getVariableSignal(LanguageCode.EN)() === '' ||
        this.getVariableSignal(LanguageCode.DE)() === ''),
  );

  placeholderValue(lang: LanguageCode): Signal<string> {
    return computed(() => {
      const key = `portal.courseSettings.topicAreaSettings.values.${this.variableName()}`;
      const value = this.translocoService.translate(
        key,
        {},
        lang.toLowerCase(),
      );
      if (value !== key) {
        return value;
      } else {
        return this.translocoService.translate(
          'portal.courseSettings.topicAreaSettings.noValue',
        );
      }
    });
  }

  getVariableSignal(lang: LanguageCode) {
    return this.courseVariablesService.getSignal(this.variableName(), lang);
  }

  copyText(fromLang: LanguageCode, toLang: LanguageCode) {
    const value = this.getVariableSignal(fromLang)();
    this.courseVariablesService.updateVariable(
      this.variableName(),
      value,
      toLang,
    );
  }

  onResetToNull(): void {
    this.courseVariablesService.updateVariable(
      this.variableName(),
      '',
      this.languageCodeDE,
    );

    this.courseVariablesService.updateVariable(
      this.variableName(),
      '',
      this.languageCodeEN,
    );
  }
}
